import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { debounce } from "lodash";

import { AlertContext, AuthContext } from "context/providers";

import JobListingCard from "components/JobListingCard";
import { Button } from "components/ui/button";
import { ChevronRight } from "lucide-react";
import { getCustomSearchClient } from "utils/helpers/search";
import {
  ClearRefinements,
  Configure,
  connectStateResults,
  Hits,
  InstantSearch,
  Pagination,
} from "react-instantsearch-dom";
import JobSearchFilters from "components/JobSearchFilters";
import { ExpandCollapseAllControl } from "components/SearchFilters/components";
import { FilterPanelProvider } from "context/providers";
import { useScrollToTopOnChange } from "hooks/useScrollToTopOnChange";
import {
  POST_GOOGLE_AUTH_REDIRECT,
  ROWS_PER_PAGE_ON_PAGINATED_TABLE,
} from "lookup";
import { cn } from "utils";
import "components/Applications/index.css";

const CustomStats = connectStateResults(
  ({ searchResults, hideProcessingTimeMS }) => {
    const { nbHits, totalRelation, processingTimeMS } = searchResults ?? {};

    const rawResultsProcessingTimesMS = searchResults?._rawResults?.map(
      ({ processingTimeMS }) => processingTimeMS
    );

    // Instant search might trigger several requests this are handled in parallel by searchKit with open-search `_msearch`
    // instant search `searchResults.processingTimeMS` is the sum of the `processingTimeMS` of each request.
    // Since they are handled in parallel we are interested only in the max processingTimeMS.
    const maxProcessingTimeMs = rawResultsProcessingTimesMS
      ? Math.max(...rawResultsProcessingTimesMS)
      : undefined;

    const plural = `${nbHits > 1 ? "s" : ""}`;

    const plusText = totalRelation === "gte" ? "+" : "";

    return (
      <div className="ais-Stats flex gap-x-2">
        <span className="ais-Stats-text">
          {nbHits}
          {plusText} result{plural} found
          {!hideProcessingTimeMS
            ? ` in ${maxProcessingTimeMs ?? processingTimeMS}ms`
            : ""}
        </span>
      </div>
    );
  }
);

export default function JobOppListV2() {
  const { user: loggedInUser } = useContext(AuthContext);

  const history = useHistory();

  const isAdminGroup = useMemo(
    () =>
      loggedInUser?.attributes?.groups?.includes(
        process.env.REACT_APP_COGNITO_ADMIN_GROUP
      ),
    [loggedInUser]
  );

  const [query, setQuery] = useState("");
  const [searchState, setSearchState] = useState({
    refinementList: {
      status: ["ACTIVE"],
      "torcOwner.username": [loggedInUser.username],
    },
  });
  const [showFilters, setShowFilters] = useState(true);
  const { clearAlerts } = useContext(AlertContext);

  const handleInputChangeDebounce = debounce(({ target }) => {
    setQuery(target.value);
  }, 350);

  useEffect(() => {
    clearAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If we are returning from a Google Auth scenario
  // AND we previously had a specific path we wanted to visit before we
  // were redirected to Google Auth, this useEffect() is to redirect the user
  // to that previous path
  useEffect(() => {
    const savedRedirectPath = window.sessionStorage.getItem(
      POST_GOOGLE_AUTH_REDIRECT
    );

    if (savedRedirectPath && savedRedirectPath.length > 0) {
      window.sessionStorage.removeItem(POST_GOOGLE_AUTH_REDIRECT);

      history.replace(savedRedirectPath);
    }
  }, [history]);

  useScrollToTopOnChange(searchState.refinementList, searchState.page);

  return (
    <InstantSearch
      searchClient={getCustomSearchClient()}
      indexName={process.env.REACT_APP_OS_JOB_INDEX}
      searchState={searchState}
      onSearchStateChange={setSearchState}
    >
      <div data-testid="jobs-landing-page" className="w-full flex flex-column">
        <div
          data-testid="jobs-filters-panel"
          className={cn(
            "transition-width duration-300 ease-in-out",
            "relative border-r-2",
            {
              "w-1/4": showFilters,
              "w-0": !showFilters,
            }
          )}
        >
          <Button
            data-testid="toggle-filters-button"
            className={cn(
              "absolute -top-10 left-0 border-2 rounded-none rounded-tr-md z-50"
            )}
            onClick={() => setShowFilters((current) => !current)}
            variant="ghost"
            size="icon"
          >
            <ChevronRight
              className={cn("transition-transform", {
                "rotate-180": showFilters,
                "rotate-0": !showFilters,
              })}
            />
          </Button>

          <div className="sticky top-0 z-10 flex h-[calc(100vh-64px)] min-h-0 flex-col overflow-hidden pt-3">
            <FilterPanelProvider>
              <div className="flex min-h-0 flex-1 flex-col gap-2 px-4">
                <h2 className="shrink-0 text-xl font-bold text-slate-800">
                  Filters
                </h2>

                <div className="flex shrink-0 items-center gap-6">
                  <ClearRefinements
                    className="filter-sidebar-btn flex-1"
                    translations={{ reset: "Clear Filters" }}
                    transformItems={(items) =>
                      items.filter(({ attribute }) => attribute)
                    }
                  />
                  <ExpandCollapseAllControl />
                </div>

                <div className="-mx-4 mt-2 min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-16 filter-sidebar-panels customScrollBar">
                  <JobSearchFilters
                    searchState={searchState}
                    showCustomerFilter={isAdminGroup}
                    onSearchInputChange={handleInputChangeDebounce}
                    query={query}
                  />
                </div>
              </div>
            </FilterPanelProvider>
          </div>
        </div>

        <div
          data-testid="jobs-content-area"
          className={cn("transition-width duration-300 ease-in-out", {
            "w-[100%]": !showFilters,
            "w-[75%]": showFilters,
          })}
        >
          <div className="flex flex-col items-center gap-y-8 pb-8">
            <div className="flex items-baseline justify-between">
              <h3 data-testid="jobs-heading" className="text-2xl font-bold p-4">
                Job Search
              </h3>
              <div data-testid="jobs-stats">
                <CustomStats />
              </div>
            </div>
            <div className="max-w-[790px] flex flex-col w-full gap-y-4">
              <div data-testid="jobs-list">
                <Hits hitComponent={JobListingCard} className="w-full" />
              </div>
              <div
                data-testid="jobs-pagination"
                className="flex w-full justify-between items-center px-2"
              >
                <Pagination />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Configure hitsPerPage={ROWS_PER_PAGE_ON_PAGINATED_TABLE} query={query} />
    </InstantSearch>
  );
}
